import {
    Directive,
    ElementRef,
    OnInit,
    OnDestroy,
    AfterViewInit,
    inject,
    HostListener
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
    selector: '[appCurrencyInput]',
    standalone: true
})
export class CurrencyInputDirective
    implements OnInit, AfterViewInit, OnDestroy {

    private readonly el =
        inject<ElementRef<HTMLInputElement>>(ElementRef);

    private readonly control =
        inject(NgControl, { self: true });

    private subscription!: Subscription;

    ngOnInit() {
        const control = this.control.control;
        if (!control) return;

        if (control.value === null || control.value === undefined) {
            control.setValue(0, { emitEvent: false });
            this.formatNumber(0);
        } else {
            this.formatNumber(control.value);
        }

        this.subscription =
            control.valueChanges.subscribe(value => {

                if (document.activeElement === this.el.nativeElement) {
                    return;
                }

                if (value === null || value === undefined) {
                    this.control.control?.setValue(
                        0,
                        { emitEvent: false }
                    );
                    this.formatNumber(0);
                } else {
                    this.formatNumber(value);
                }
            });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            const value =
                this.control.control?.value;

            if (
                document.activeElement !== this.el.nativeElement
            ) {
                if (value === null || value === undefined) {
                    this.control.control?.setValue(
                        0,
                        { emitEvent: false }
                    );
                    this.formatNumber(0);
                } else {
                    this.formatNumber(value);
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string) {

        const rawValue =
            value.replace(/[^\d.-]/g, '');

        this.el.nativeElement.value = rawValue;

        if (
            rawValue === '' ||
            rawValue === '-' ||
            rawValue === '.' ||
            rawValue === '-.'
        ) {
            this.control.control?.setValue(
                null,
                { emitEvent: false }
            );
            return;
        }

        this.control.control?.setValue(
            rawValue,
            { emitEvent: false }
        );
    }

    @HostListener('focus')
    onFocus() {

        const el = this.el.nativeElement;
        const value = el.value?.trim();

        if (!value || value === '0.00') {
            el.value = '';
            return;
        }

        const numericString =
            value.replace(/[^0-9.-]/g, '');

        el.value = numericString;

        setTimeout(() => el.select());
    }

    @HostListener('blur')
    onBlur() {

        const value =
            this.control.control?.value;

        if (value === null || value === undefined || value === '') {
            this.control.control?.setValue(
                0,
                { emitEvent: false }
            );
            this.formatNumber(0);
            return;
        }

        const numericValue =
            typeof value === 'number'
                ? value
                : parseFloat(value);

        if (isNaN(numericValue)) {
            this.control.control?.setValue(
                0,
                { emitEvent: false }
            );
            this.formatNumber(0);
            return;
        }

        this.control.control?.setValue(
            numericValue,
            { emitEvent: false }
        );

        this.formatNumber(numericValue);
    }

    private formatNumber(
        value: number | string
    ) {

        const numericValue =
            typeof value === 'number'
                ? value
                : parseFloat(value);

        const safeValue =
            isNaN(numericValue) ? 0 : numericValue;

        const formattedValue =
            new Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(safeValue);

        this.el.nativeElement.value =
            formattedValue;
    }
}
